import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { setActiveQuestion } from '../../../store/actions/PracticeTestActions';
import styles from './QuestionSidebar.scss';

const QuestionSidebar = ({ assessment, questions_index, activeQuestionId }) => {
  const { t } = useTranslation('practice_tests');
  const dispatch = useDispatch();
  const [selectedSectionId, setSelectedSectionId] = useState(null);

  // Sort sections by their order field
  const sectionsList = [...(assessment.sections || [])].sort((a, b) => a.order - b.order);

  // Auto-select first section on mount
  useEffect(() => {
    if (sectionsList.length > 0 && !selectedSectionId) {
      setSelectedSectionId(sectionsList[0].id);
    }
  }, [sectionsList.length, selectedSectionId]);

  // Auto-select section that contains active question only on initial load
  useEffect(() => {
    if (activeQuestionId && sectionsList.length > 0 && !selectedSectionId) {
      const activeSection = sectionsList.find(section => {
        const sectionProblemIds = section.problems?.map(p => String(p.id)) || [];
        const sectionMcqIds = section.mcqs?.map(m => String(m.id)) || [];
        const allSectionQuestionIds = [...sectionProblemIds, ...sectionMcqIds];
        return allSectionQuestionIds.includes(String(activeQuestionId));
      });

      if (activeSection) {
        setSelectedSectionId(activeSection.id);
      }
    }
  }, [activeQuestionId, sectionsList]);

  return (
    <div
      style={{ padding: '0px', background: 'white' }}
      data-test-id="question-sidebar"
      data-specific-id="sidebar-container"
    >
      <div
        className={styles['sidebar']}
        data-test-id="sidebar-content"
        data-specific-id="sidebar-main"
      >
        {/* Check if test has sections */}
        {sectionsList && sectionsList.length > 0 ? (
          // Show sections
          sectionsList.map(section => {
            // Get questions for this section from section's problems and mcqs arrays
            // Convert IDs to strings for comparison since questions_index contains string keys
            const sectionProblemIds = section.problems?.map(p => String(p.id)) || [];
            const sectionMcqIds = section.mcqs?.map(m => String(m.id)) || [];
            const allSectionQuestionIds = [...sectionProblemIds, ...sectionMcqIds];

            const sectionQuestions = questions_index.filter(ques_id => {
              return allSectionQuestionIds.includes(String(ques_id));
            });

            return (
              <div
                key={section.id}
                className={styles['sidebar-section']}
                data-test-id={`section-${section.id}`}
                data-specific-id={`sidebar-section-${section.id}`}
              >
                <div
                  className={`${styles['section-title']} ${
                    selectedSectionId === section.id ? styles['active'] : ''
                  }`}
                  onClick={() => {
                    setSelectedSectionId(section.id);
                    // Switch to first question of the selected section
                    const sectionProblemIds = section.problems?.map(p => String(p.id)) || [];
                    const sectionMcqIds = section.mcqs?.map(m => String(m.id)) || [];
                    const allSectionQuestionIds = [...sectionProblemIds, ...sectionMcqIds];
                    if (allSectionQuestionIds.length > 0) {
                      dispatch(setActiveQuestion({ active_question: allSectionQuestionIds[0] }));
                    }
                  }}
                  data-test-id={`section-title-${section.id}`}
                  data-specific-id={`section-header-${section.id}`}
                  data-action="toggle-section"
                >
                  <span data-test-id={`section-name-${section.id}`}>{section.title}</span>
                </div>

                {selectedSectionId === section.id && (
                  <div
                    className={styles['questions-list']}
                    data-test-id={`questions-list-${section.id}`}
                    data-specific-id={`section-questions-${section.id}`}
                  >
                    {sectionQuestions.map((ques_id, localIndex) => {
                      const isActive = String(ques_id) === String(activeQuestionId);
                      return (
                        <div
                          key={ques_id}
                          className={`${styles['question-item']} ${
                            isActive ? styles['active'] : ''
                          }`}
                          onClick={() => dispatch(setActiveQuestion({ active_question: ques_id }))}
                          data-test-id={`question-item-${ques_id}`}
                          data-specific-id={`sidebar-question-${ques_id}`}
                          data-action="select-question"
                        >
                          <div className={styles['question-title']}>
                            <div className={styles['question-number']}>Q{localIndex + 1}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        ) : (
          // No sections - show all questions
          <div
            className={styles['sidebar-section']}
            data-test-id="no-sections-sidebar"
            data-specific-id="general-questions-section"
          >
            <div className={`${styles['section-title']} ${styles['active']}`}>
              <span data-test-id="questions-title">
                {t('practice_tests.test_result.questions')}
              </span>
            </div>
            <div
              className={styles['questions-list']}
              data-test-id="all-questions-list"
              data-specific-id="general-questions-list"
            >
              {questions_index.map((ques_id, localIndex) => {
                const isActive = String(ques_id) === String(activeQuestionId);
                return (
                  <div
                    key={ques_id}
                    className={`${styles['question-item']} ${isActive ? styles['active'] : ''}`}
                    onClick={() => dispatch(setActiveQuestion({ active_question: ques_id }))}
                    data-test-id={`question-item-${ques_id}`}
                    data-specific-id={`sidebar-question-${ques_id}`}
                    data-action="select-question"
                  >
                    <div className={styles['question-title']}>
                      <div className={styles['question-number']}>Q{localIndex + 1}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuestionSidebar;
